import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  Menu,
  MenuItem,
  Paper,
  Snackbar,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import SearchIcon from '@mui/icons-material/Search';
import ToggleOnOutlinedIcon from '@mui/icons-material/ToggleOnOutlined';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { apiErrorMessage, platformIntegrationsService } from '../../services/api';
import { EmptyState, PageHeader, TableSkeleton } from '../../components/ui';
import { IntegrationCategory, IntegrationColumn, IntegrationMatrix } from '../../types';

/**
 * Super admin control panel: which integrations each shop may use.
 *
 * A switch is permission, not setup — turning one off stops new use (new
 * payments, connecting, searching and ordering, imports, webhook deliveries)
 * while keeping the shop's own credentials, so turning it back on needs no
 * reconnect. Money and orders already in flight still settle.
 */

const CATEGORY_LABELS: Record<IntegrationCategory, string> = {
  payments: 'Payments',
  parts: 'Parts',
  platform: 'Platform',
};

/** What switching an integration off does, in the shop's terms. */
const CONSEQUENCES: Record<IntegrationCategory, string> = {
  payments:
    'The shop stops taking new card payments through it. Payments already in progress still settle, and payments it took can still be refunded.',
  parts:
    'The shop can no longer connect, search or order through it. Orders already placed are kept and can still be received.',
  platform:
    'Nothing new is sent or accepted through it; deliveries already queued are dropped.',
};

interface PendingChange {
  /** Null = every shop. */
  shop: { id: string; name: string } | null;
  integration: IntegrationColumn;
  enabled: boolean;
}

const PlatformIntegrationsPage: React.FC = () => {
  const [matrix, setMatrix] = useState<IntegrationMatrix | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [saving, setSaving] = useState<string | null>(null);

  const [pending, setPending] = useState<PendingChange | null>(null);
  const [reason, setReason] = useState('');
  const [menu, setMenu] = useState<{ anchor: HTMLElement; integration: IntegrationColumn } | null>(
    null,
  );

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setMatrix(await platformIntegrationsService.matrix());
      setError(null);
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not load shop integrations.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const shops = useMemo(() => {
    const term = search.trim().toLowerCase();
    return (matrix?.shops ?? []).filter((shop) => !term || shop.name.toLowerCase().includes(term));
  }, [matrix, search]);

  /** Columns grouped for the two-row header. */
  const groups = useMemo(() => {
    const byCategory = new Map<IntegrationCategory, IntegrationColumn[]>();
    for (const column of matrix?.integrations ?? []) {
      byCategory.set(column.category, [...(byCategory.get(column.category) ?? []), column]);
    }
    return [...byCategory.entries()];
  }, [matrix]);

  const columns = groups.flatMap(([, list]) => list);

  const enabledCount = (key: string) =>
    (matrix?.shops ?? []).filter((shop) => shop.access[key]?.enabled).length;

  const apply = async (change: PendingChange, why?: string) => {
    const label = change.shop ? `${change.integration.name} for ${change.shop.name}` : change.integration.name;
    setSaving(change.shop ? `${change.shop.id}:${change.integration.key}` : change.integration.key);
    setError(null);
    try {
      const next = change.shop
        ? await platformIntegrationsService.setForShop(
            change.shop.id,
            change.integration.key,
            change.enabled,
            why,
          )
        : await platformIntegrationsService.setForAllShops(
            change.integration.key,
            change.enabled,
            why,
          );
      setMatrix(next);
      setNotice(
        `${label} turned ${change.enabled ? 'on' : 'off'}${change.shop ? '' : ' for every shop'}`,
      );
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not change the integration.'));
    } finally {
      setSaving(null);
    }
  };

  /** Turning on is immediate; turning off (or any bulk change) asks first. */
  const request = (change: PendingChange) => {
    if (change.enabled && change.shop) {
      void apply(change);
      return;
    }
    setReason('');
    setPending(change);
  };

  const confirm = async () => {
    if (!pending) return;
    const change = pending;
    setPending(null);
    await apply(change, change.enabled ? undefined : reason.trim() || undefined);
  };

  return (
    <Container maxWidth="xl" sx={{ py: { xs: 3, md: 5 } }}>
      <PageHeader
        eyebrow="Platform"
        title="Shop integrations"
        subtitle="Choose which integrations each shop may use. Turning one off stops new use straight away and keeps the shop's own setup, so turning it back on needs no reconnect."
      />

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <TextField
        size="small"
        placeholder="Find a shop"
        value={search}
        onChange={(event) => setSearch(event.target.value)}
        sx={{ mb: 2, width: { xs: '100%', sm: 320 } }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon fontSize="small" />
            </InputAdornment>
          ),
        }}
      />

      <Paper elevation={0} sx={{ borderRadius: 3, overflow: 'hidden' }}>
        {!loading && matrix && matrix.shops.length === 0 ? (
          <EmptyState
            icon={<ToggleOnOutlinedIcon />}
            title="No shops yet"
            description="Integrations can be switched per shop once shops exist."
          />
        ) : (
          <TableContainer sx={{ overflowX: 'auto' }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell rowSpan={2} sx={{ minWidth: 200, verticalAlign: 'bottom' }}>
                    Shop
                  </TableCell>
                  {groups.map(([category, list]) => (
                    <TableCell
                      key={category}
                      colSpan={list.length}
                      align="center"
                      sx={{ fontWeight: 700, borderLeft: 1, borderColor: 'divider' }}
                    >
                      {CATEGORY_LABELS[category]}
                    </TableCell>
                  ))}
                </TableRow>
                <TableRow>
                  {columns.map((column, index) => {
                    const firstInGroup =
                      index === 0 || columns[index - 1].category !== column.category;
                    return (
                      <TableCell
                        key={column.key}
                        align="center"
                        sx={{
                          minWidth: 140,
                          verticalAlign: 'top',
                          ...(firstInGroup ? { borderLeft: 1, borderColor: 'divider' } : {}),
                        }}
                      >
                        <Stack spacing={0.5} alignItems="center">
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Tooltip title={column.description}>
                              <Typography variant="body2" fontWeight={600}>
                                {column.name}
                              </Typography>
                            </Tooltip>
                            <IconButton
                              size="small"
                              aria-label={`${column.name} options`}
                              onClick={(event) =>
                                setMenu({ anchor: event.currentTarget, integration: column })
                              }
                            >
                              <MoreVertIcon fontSize="small" />
                            </IconButton>
                          </Box>
                          {matrix && (
                            <Typography variant="caption" color="text.secondary">
                              On for {enabledCount(column.key)} of {matrix.shops.length}
                            </Typography>
                          )}
                          {!column.serverConfigured && (
                            <Tooltip title="The server is not set up for this integration yet (missing keys or partner agreement). Shops cannot use it even where it is on.">
                              <Chip
                                size="small"
                                color="warning"
                                variant="outlined"
                                icon={<WarningAmberIcon />}
                                label="Server not set up"
                              />
                            </Tooltip>
                          )}
                        </Stack>
                      </TableCell>
                    );
                  })}
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableSkeleton rows={4} columns={columns.length + 1 || 4} />
                ) : (
                  shops.map((shop) => (
                    <TableRow key={shop.id} hover>
                      <TableCell>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Typography fontWeight={600}>{shop.name}</Typography>
                          {!shop.isActive && <Chip size="small" variant="outlined" label="Inactive" />}
                        </Stack>
                      </TableCell>
                      {columns.map((column, index) => {
                        const cell = shop.access[column.key];
                        const firstInGroup =
                          index === 0 || columns[index - 1].category !== column.category;
                        const busy = saving === `${shop.id}:${column.key}` || saving === column.key;
                        return (
                          <TableCell
                            key={column.key}
                            align="center"
                            sx={firstInGroup ? { borderLeft: 1, borderColor: 'divider' } : undefined}
                          >
                            {/* describeChild: the tooltip describes the switch
                                rather than replacing its accessible name. */}
                            <Tooltip
                              describeChild
                              title={
                                cell?.reason
                                  ? `Off: ${cell.reason}`
                                  : cell?.overridden
                                    ? 'Set by a super admin'
                                    : 'Default'
                              }
                            >
                              <Switch
                                size="small"
                                checked={Boolean(cell?.enabled)}
                                disabled={busy}
                                slotProps={{
                                  input: {
                                    'aria-label': `${column.name} for ${shop.name}`,
                                  },
                                }}
                                onChange={(event) =>
                                  request({
                                    shop: { id: shop.id, name: shop.name },
                                    integration: column,
                                    enabled: event.target.checked,
                                  })
                                }
                              />
                            </Tooltip>
                            <Typography
                              variant="caption"
                              display="block"
                              color={cell?.connected ? 'text.primary' : 'text.secondary'}
                            >
                              {cell?.connected ? 'Set up' : 'Not set up'}
                            </Typography>
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  ))
                )}
                {!loading && matrix && shops.length === 0 && matrix.shops.length > 0 && (
                  <TableRow>
                    <TableCell colSpan={columns.length + 1}>
                      <Typography color="text.secondary">No shop matches “{search}”.</Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      <Menu anchorEl={menu?.anchor} open={Boolean(menu)} onClose={() => setMenu(null)}>
        <MenuItem
          onClick={() => {
            if (menu) request({ shop: null, integration: menu.integration, enabled: true });
            setMenu(null);
          }}
        >
          Turn on for every shop
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (menu) request({ shop: null, integration: menu.integration, enabled: false });
            setMenu(null);
          }}
        >
          Turn off for every shop
        </MenuItem>
      </Menu>

      <Dialog open={Boolean(pending)} onClose={() => setPending(null)} maxWidth="sm" fullWidth>
        {pending && (
          <>
            <DialogTitle>
              Turn {pending.enabled ? 'on' : 'off'} {pending.integration.name}
              {pending.shop ? ` for ${pending.shop.name}` : ' for every shop'}?
            </DialogTitle>
            <DialogContent>
              <Stack spacing={2}>
                {pending.enabled ? (
                  <Typography color="text.secondary">
                    Every existing shop will be allowed to use {pending.integration.name}. Shops
                    created later get the default.
                  </Typography>
                ) : (
                  <>
                    <Typography color="text.secondary">
                      {CONSEQUENCES[pending.integration.category]} The shop's own setup is kept, so
                      turning it back on needs no reconnect.
                      {pending.shop ? '' : ' Shops created later get the default.'}
                    </Typography>
                    <TextField
                      label="Reason (shown to the shop's admins)"
                      value={reason}
                      onChange={(event) => setReason(event.target.value)}
                      inputProps={{ maxLength: 500 }}
                      placeholder="e.g. Plan does not include card payments"
                      autoFocus
                    />
                  </>
                )}
              </Stack>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2.5 }}>
              <Button onClick={() => setPending(null)}>Cancel</Button>
              <Button
                variant="contained"
                color={pending.enabled ? 'primary' : 'error'}
                onClick={confirm}
              >
                Turn {pending.enabled ? 'on' : 'off'}
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      <Snackbar
        open={Boolean(notice)}
        autoHideDuration={4000}
        onClose={() => setNotice(null)}
        message={notice}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
      <Box sx={{ height: 24 }} />
    </Container>
  );
};

export default PlatformIntegrationsPage;
